/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from "next/image";
import { memo, useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import WatchlistIcon from "src/assets/images/Watchlist.svg";
import DateTimePicker from "src/components/dashboard/DateTimePicker";
import PortfolioFolderSelector, {
  PortfolioFolder,
} from "src/components/portfolio/PortfolioFolder";
import AddFundModal from "src/components/portfolio/addFundModal";
import { fetchWrapper, StockTableData, Watchlist } from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { formatCurrencyWithLocalString } from "src/helpers/numberFormatter";
import { alertService } from "src/services";
import { Company, SharedSearchedElement } from "../../common/search";
import { WatchlistData } from "../watchlist/WatchlistTable";

const MAX_QUANTITY = 10000000;
const MIN_QUANTITY = 1;
const MAX_PRICE = 10000000;
const MIN_PRICE = 0.01;
const MAX_PERCENTAGE = 1000;
const MIN_PERCENTAGE = -100;

const validateQuantity = (quantity: number): string | undefined => {
  if (quantity === undefined) return "Quantity is required";
  if (quantity < MIN_QUANTITY)
    return `Quantity must be at least ${MIN_QUANTITY}`;
  if (quantity > MAX_QUANTITY)
    return `Quantity cannot exceed ${MAX_QUANTITY.toLocaleString()}`;
  if (!Number.isInteger(quantity)) return "Quantity must be a whole number";
  return undefined;
};

const validatePrice = (
  price: number,
  fieldName: string,
): string | undefined => {
  if (price === undefined) return `${fieldName} is required`;
  if (price < MIN_PRICE) return `Price must be at least ₹${MIN_PRICE}`;
  if (price > MAX_PRICE)
    return `Price cannot exceed ₹${MAX_PRICE.toLocaleString()}`;
  if (!/^\d+(\.\d{1,2})?$/.test(price.toString()))
    return "Price must have at most 2 decimal places";
  return undefined;
};

const validatePercentage = (
  percentage: number,
  fieldName: string,
): string | undefined => {
  if (percentage < MIN_PERCENTAGE)
    return `${fieldName} cannot be less than ${MIN_PERCENTAGE}%`;
  if (percentage > MAX_PERCENTAGE)
    return `${fieldName} cannot exceed ${MAX_PERCENTAGE}%`;
  return undefined;
};

export const InvestmentSummary = ({
  investmentImg,
  investmentAltText,
  quantity,
  buyPrice,
  currentPrice,
  liquidFunds,
  onAddFundsClick,
}: {
  investmentImg: string;
  investmentAltText: string;
  quantity: number;
  buyPrice: number;
  currentPrice: number;
  liquidFunds?: number;
  onAddFundsClick?: (deficit: number) => void;
}) => {
  const totalInvestment = quantity * buyPrice;
  const unrealizedPNL = currentPrice - totalInvestment;
  const returnPercentage =
    buyPrice > 0 && totalInvestment > 0
      ? (() => {
          const value =
            ((currentPrice - totalInvestment) / totalInvestment) * 100;
          return isNaN(value) ? 0 : value;
        })()
      : 0;

  return (
    <div className="bg-white dark:bg-[#1E1E2D] rounded-lg shadow-lg border border-[#e0e0e0] dark:border-[#333] p-4 font-figtree mb-2">
      <div className="flex items-center gap-2 mb-0">
        <Image
          src={investmentImg}
          alt={investmentAltText}
          className="w-5 h-5 dark:invert dark:brightness-0 dark:contrast-100"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Investment Summary
        </span>
      </div>
      {typeof liquidFunds === "number" && (
        <span
          className={`ml-7 mb-3 block text-xs ${
            liquidFunds < 0
              ? "text-red-500 font-bold cursor-pointer hover:underline"
              : "text-gray-500 dark:text-gray-400"
          }`}
          style={{ display: "table" }}
          onClick={() => {
            if (liquidFunds < 0 && onAddFundsClick) {
              onAddFundsClick(Math.abs(liquidFunds));
            }
          }}
        >
          {liquidFunds < 0
            ? `You need ${formatCurrencyWithLocalString(Math.abs(liquidFunds))} more`
            : `Liquid Funds: ${formatCurrencyWithLocalString(liquidFunds)}`}
        </span>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <SummaryCard
          label="Total Investment"
          value={`${formatCurrencyWithLocalString(totalInvestment.toFixed(2))}`}
        />
        <SummaryCard
          label="Current Value"
          value={`${formatCurrencyWithLocalString(currentPrice.toFixed(2))}`}
        />
        <SummaryCard
          label="Unrealized P&L"
          value={`₹${unrealizedPNL.toFixed(2)}`}
          isPositive={unrealizedPNL === 0 ? undefined : unrealizedPNL > 0}
        />
        <SummaryCard
          label="Return %"
          value={`${returnPercentage.toFixed(2)}%`}
          isPositive={returnPercentage === 0 ? undefined : returnPercentage > 0}
        />
      </div>
    </div>
  );
};

const SummaryCard = ({
  label,
  value,
  isPositive,
}: {
  label: string;
  value: string;
  isPositive?: boolean;
}) => (
  <div className="flex flex-col p-2 rounded-lg bg-gradient-to-br from-[#f4f6fb] to-[#e9ebf3] dark:from-[#2a2d3e] dark:to-[#1f2230] shadow-sm">
    <span className="text-gray-600 dark:text-gray-400">{label}</span>
    <span
      className={`${
        isPositive !== undefined
          ? isPositive
            ? "dark:text-green-500 text-green-600"
            : "dark:text-red-500 text-red-600"
          : "text-gray-900 dark:text-white"
      } font-medium`}
    >
      {value}
    </span>
  </div>
);

const PriceInput = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: string;
  disabled?: boolean;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:outline-none focus-visible:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/40 focus:ring-inset dark:text-white text-sm font-figtree ${
        disabled ? "bg-gray-200 dark:bg-gray-800 cursor-not-allowed" : ""
      }`}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const AlertFrequencyRadio = ({
  value,
  label,
  checked,
  onChange,
  disabled,
}: {
  value: string;
  label: string;
  checked: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center gap-2">
    <input
      type="radio"
      id={value}
      name="frequency"
      value={value}
      checked={checked}
      onChange={(e) => onChange(e.target.value)}
      className={`h-4 w-4 border-2 appearance-none rounded-full relative 
        before:content-[''] before:absolute before:top-1/2 before:left-1/2 
        before:transform before:-translate-x-1/2 before:-translate-y-1/2 
        before:w-2 before:h-2 before:rounded-full before:opacity-0 
        ${
          disabled
            ? "border-gray-300 bg-gray-100 cursor-not-allowed"
            : "border-gray-300 focus:ring-[#00A85F] cursor-pointer checked:border-[#00A85F] before:bg-[#00A85F] checked:before:opacity-100"
        }
      `}
      disabled={disabled}
    />
    <label
      htmlFor={value}
      className={`text-sm font-medium ${
        disabled
          ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
          : "text-gray-700 dark:text-gray-300 cursor-pointer"
      }`}
    >
      {label}
    </label>
  </div>
);

interface ModalListProps {
  selected_date?: Date;
  investmentImg?: string;
  investmentAltText?: string;
  notes: string;
  heading: string;
  handleOnClick: () => void;
  handleCancelClick?: () => void;
  selectedStock: StockTableData | WatchlistData | null;
  quantity?: number;
  setQuantity?: React.Dispatch<React.SetStateAction<number>>;
  buyPrice?: number;
  setBuyPrice?: React.Dispatch<React.SetStateAction<number>>;
  returnPercentage?: string;
  setNotes: React.Dispatch<React.SetStateAction<string>>;
  isWatchlist?: boolean;
  watchlist?: Watchlist;
  setWatchlist?: React.Dispatch<React.SetStateAction<Watchlist>>;
  StatsCard?: boolean;
  getData?: () => Promise<void>;
  getTabData?: (date: string) => Promise<void>;
  isModalOpen?: boolean;
  isDashboard?: boolean;
  isWishlistViewLayout?: boolean;
  isBuyStock?: boolean;
  date?: string;
  updatedStock?: any;
  isPortfolio?: boolean;
  targetIdValue?: string | number | null;
  preSelectedPortfolioId?: string | number | (string | number)[] | null;
  onSuccess?: (folder: PortfolioFolder) => void;
}

const ListModal = (props: ModalListProps) => {
  const {
    investmentImg,
    investmentAltText,
    notes,
    heading,
    selectedStock,
    quantity = 0,
    setQuantity,
    buyPrice = 0,
    setBuyPrice,
    setNotes,
    handleOnClick,
    getData,
    isWatchlist,
    watchlist,
    setWatchlist,
    StatsCard,
    isModalOpen,
    isDashboard,
    updatedStock,
    selected_date,
    targetIdValue,
    isBuyStock,
    preSelectedPortfolioId,
    isPortfolio,
    onSuccess,
  } = props;

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    selected_date ?? new Date(),
  );
  const user = getLocalStorageUser();
  const token = user.token;

  const [errors, setErrors] = useState<{
    above?: string;
    below?: string;
    quantity?: string;
    buyPrice?: string;
    notes?: string;
    date?: string;
  }>({});

  const maxNotesLength = 200;
  const [search, setSearch] = useState("");
  const [autoComplete, setAutoComplete] = useState<boolean>(false);
  const defaultValue: Company = { symbol: "", company_name: "" };
  const [filteredData, setFilteredData] = useState<Company[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  const [records, setRecords] = useState<StockTableData[]>([]);
  const [buyPriceInput, setBuyPriceInput] = useState<string>(
    buyPrice?.toString() || "",
  );
  const [quantityInput, setQuantityInput] = useState<string>(
    quantity?.toString() || "",
  );

  const currentStockPrice =
    records[0]?.ltp || (selectedStock && selectedStock?.ltp) || 0;
  const currentStockSymbol =
    records[0]?.symbol || (selectedStock && selectedStock.symbol) || "";
  const currentValue = quantity * currentStockPrice;
  const listingDate = records[0]?.listing_date ?? null;
  const selectedDateStr = selectedDate
    ? moment(selectedDate).tz("Asia/Kolkata").format("YYYY-MM-DD")
    : null;
  const listingDateError =
    listingDate && selectedDateStr && selectedDateStr < listingDate
      ? `Cannot add this stock before the listing date.`
      : undefined;
  // const returnPercentageValue =
  //   buyPrice > 0
  //     ? (((currentStockPrice - buyPrice) / buyPrice) * 100).toFixed(2)
  //     : "0.00";

  const [abovePriceInput, setAbovePriceInput] = useState("");
  const [abovePercentInput, setAbovePercentInput] = useState("");
  const [belowPriceInput, setBelowPriceInput] = useState("");
  const [belowPercentInput, setBelowPercentInput] = useState("");
  const [portfolioFolders, setPortfolioFolders] = useState<PortfolioFolder[]>(
    [],
  );
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [showAddFundModal, setShowAddFundModal] = useState(false);
  const [addFundAmount, setAddFundAmount] = useState<number>(0);

  const handleCloseAddFundModal = () => {
    setShowAddFundModal(false);
    setAddFundAmount(0);
  };

  const handleSuccessAddFund = (folder: PortfolioFolder) => {
    setPortfolioFolders((prev) =>
      prev.map((p) =>
        p.id === folder.id ? { ...p, investment: folder.investment } : p,
      ),
    );
  };
  const getCompanyInfo = async (stockName: string) => {
    const response = await fetchWrapper.get(
      "/api/dashboard/company_info?name=" + encodeURIComponent(stockName),
      undefined,
      { Authorization: `Bearer ${token}` },
    );
    return response;
  };

  const getSelectedCompanyData = async (search: string) => {
    const response = await fetchWrapper.get(
      `/api/users/watchlists/find?symbol=${encodeURIComponent(search)}`,
      undefined,
      { Authorization: `Bearer ${token}` },
    );
    setRecords(response.data);
  };
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isModalOpen || !selectedStock?.symbol) return;
    getSelectedCompanyData(selectedStock.symbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, selectedStock?.symbol]);

  const handleOnChangeSearchInput = async (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFilteredData([]);
    debounceRef.current = setTimeout(async () => {
      if (value === "") {
        setFilteredData([]);
        setRecords([]);
        if (setBuyPrice) setBuyPrice(0);
        if (setQuantity) setQuantity(1);
        setBuyPriceInput("0");
        setQuantityInput("1");
        return;
      }
      const response = await getCompanyInfo(value);
      const uniqueStocks = Array.from(
        new Map(
          response.data.map((stock: Company) => [stock.symbol, stock]),
        ).values(),
      ) as Company[];

      setFilteredData(uniqueStocks.reverse());
    }, 500);
  };

  useEffect(() => {
    if (isModalOpen && records[0]?.ltp && setBuyPrice) {
      setBuyPrice(Number(records[0].ltp));
      setBuyPriceInput(records[0].ltp.toString());
    }
  }, [isModalOpen, records, setBuyPrice]);

  useEffect(() => {
    if (isModalOpen && selectedStock && setBuyPrice) {
      setBuyPrice(Number(selectedStock.ltp));
      setBuyPriceInput(selectedStock.ltp.toString());
    }
  }, [isModalOpen, selectedStock, setBuyPrice]);

  useEffect(() => {
    if (currentStockSymbol || search === "") {
      setErrors({});
      setWatchlist?.({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStockSymbol, search]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Notes validation
    if (notes.length > maxNotesLength) {
      newErrors.notes = `Notes cannot exceed ${maxNotesLength} characters`;
    }

    if (!isWatchlist) {
      const quantityError = validateQuantity(quantity);
      const priceError = validatePrice(buyPrice, "Buy price");

      if (quantityError) newErrors.quantity = quantityError;
      if (priceError) newErrors.buyPrice = priceError;
      if (listingDateError) newErrors.date = listingDateError;
    } else if (
      watchlist &&
      (watchlist.is_recurring === "everytime" ||
        watchlist.is_recurring === "once")
    ) {
      // Watchlist validation
      const basePrice = currentStockPrice;

      if (watchlist.above_price !== undefined) {
        if (basePrice && watchlist.above_price <= Number(basePrice)) {
          newErrors.above = `Above Price must be greater than ₹${basePrice}`;
        }
        const priceError = validatePrice(watchlist.above_price, "Above price");
        if (priceError) newErrors.above = priceError;
      }

      if (watchlist.above_percentage !== undefined) {
        const percentageError = validatePercentage(
          watchlist.above_percentage,
          "Above percentage",
        );
        if (percentageError) newErrors.above = percentageError;
      }

      if (watchlist.below_price !== undefined) {
        if (basePrice && watchlist.below_price >= Number(basePrice)) {
          newErrors.below = `Below Price must be less than ₹${basePrice}`;
        }
        const priceError = validatePrice(watchlist.below_price, "Below price");
        if (priceError) newErrors.below = priceError;
      }

      if (watchlist.below_percentage !== undefined) {
        const percentageError = validatePercentage(
          watchlist.below_percentage,
          "Below percentage",
        );
        if (percentageError) newErrors.below = percentageError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const totalSelectedInvestment =
    portfolioFolders
      .filter((p) => selectedFolders.includes(String(p.id)))
      .reduce((sum, p) => sum + (Number(p.investment) || 0), 0) -
    (Number(quantityInput) || 0) * (Number(buyPriceInput) || 0);

  const handleAddToPortfolio = async () => {
    if (!validateForm()) return;

    const selectedPortfolio = portfolioFolders.find((p) =>
      selectedFolders.includes(String(p.id)),
    );

    if (!selectedPortfolio) {
      alertService.error("Please select a portfolio");
      return;
    }

    const body = {
      symbol: currentStockSymbol,
      date: selectedDate
        ? moment(selectedDate).tz("Asia/Kolkata").format("YYYY-MM-DD")
        : null,
      quantity: String(quantity),
      buy_price: String(buyPrice),
      notes: notes,
      portfolio: {
        id: selectedPortfolio.id,
        name: selectedPortfolio.name,
        investment: Number(selectedPortfolio.investment ?? 0),
      },
    };

    try {
      const response = await fetchWrapper.post(
        "/api/users/portfolio/add",
        body,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      if (response.success) {
        const storedUser = getLocalStorageUser();

        if (storedUser) {
          const updatedUser = {
            ...storedUser,
            portfolio_count: Number(response.portfolioCount),
          };

          localStorage.setItem("BetaUser", JSON.stringify(updatedUser));
        }
        if (isDashboard) {
          updatedStock(currentStockSymbol);
        }

        await onSuccess?.({
          id: selectedPortfolio.id,
          name: selectedPortfolio.name,
          investment: Number(selectedPortfolio.investment ?? 0),
        });
        handleOnClick();
        getData?.();
        alertService.success(
          `${currentStockSymbol} - Added to portfolio successfully.`,
        );
      } else {
        alertService.error(response.message);
      }
    } catch (error) {
      console.error("Request failed:", error);
      alertService.error("Something went wrong. Please try again later.");
    }
  };

  const handleAddToWatchlist = async () => {
    if (!validateForm()) return;

    const today = new Date().toISOString().split("T")[0];
    const updatedWatchlist = watchlist
      ? {
          ...watchlist,
          record_date: today,
          symbol: currentStockSymbol,
          notes: notes,
          is_recurring:
            watchlist.is_recurring === "everytime"
              ? true
              : watchlist.is_recurring === "once"
                ? false
                : "",
        }
      : undefined;

    try {
      const response = await fetchWrapper.post(
        "/api/users/watchlists/add",
        {
          ...updatedWatchlist,
          token: records[0]?.token,
          current_price: currentStockPrice,
        } as any,
        { Authorization: `Bearer ${token}` },
      );

      if (response.success) {
        const storedUser = getLocalStorageUser();

        if (storedUser) {
          const updatedUser = {
            ...storedUser,
            watchlist_count: Number(storedUser.watchlist_count ?? 0) + 1,
          };

          localStorage.setItem("BetaUser", JSON.stringify(updatedUser));
        }
        if (isDashboard) {
          if (selectedStock?.symbol) {
            updatedStock(selectedStock.symbol, true);
          }
        }
        await getData?.();
        handleOnClick();
        alertService.success(
          `${currentStockSymbol} - Added to watchlist successfully.`,
        );
      } else {
        alertService.error(response.message);
      }
    } catch (err) {
      const error = err as string;
      alertService.error(error);
      console.error("Error:", err);
    }
  };

  const roundTo2 = (num: number) => parseFloat(num.toFixed(2));

  const handleAbovePriceChange = (value: string) => {
    setAbovePriceInput(value); // Store raw input

    if (value === "" && belowPriceInput === "") {
      setWatchlist?.({
        ...watchlist!,
        above_price: undefined,
        above_percentage: undefined,
        is_recurring: undefined,
      });
      setAbovePercentInput("");
      return;
    }

    // Validate: only digits, one decimal point max, max 2 digits after decimal
    if (!/^\d*\.?\d{0,2}$/.test(value)) {
      setAbovePriceInput(value.slice(0, -1)); // Remove last invalid character
      return;
    }

    const price = parseFloat(value);
    if (isNaN(price)) return;

    const basePrice = currentStockPrice;
    if (!basePrice) return;

    const percentage = ((price - basePrice) / basePrice) * 100;
    const roundedPercent = roundTo2(percentage);

    setWatchlist?.({
      ...watchlist!,
      above_price: roundTo2(price),
      above_percentage: roundedPercent,
    });

    setAbovePercentInput(roundedPercent.toString());
  };

  const handleAbovePercentageChange = (value: string) => {
    setAbovePercentInput(value);

    if (value === "") {
      setWatchlist?.({
        ...watchlist!,
        above_price: undefined,
        above_percentage: undefined,
      });
      setAbovePriceInput("");
      return;
    }

    // Validate: optional minus, digits, one decimal point max, max 2 digits after decimal
    if (!/^-?\d*\.?\d{0,2}$/.test(value)) {
      setAbovePercentInput(value.slice(0, -1)); // Remove last invalid character
      return;
    }

    const percentage = parseFloat(value);
    if (isNaN(percentage)) return;

    const basePrice = currentStockPrice;
    if (!basePrice) return;

    const price = basePrice * (1 + percentage / 100);
    const roundedPrice = roundTo2(price);

    setWatchlist?.({
      ...watchlist!,
      above_price: roundedPrice,
      above_percentage: roundTo2(percentage),
    });

    setAbovePriceInput(roundedPrice.toString());
  };

  const handleBelowPriceChange = (value: string) => {
    setBelowPriceInput(value);

    if (value === "" && abovePriceInput === "") {
      setWatchlist?.({
        ...watchlist!,
        below_price: undefined,
        below_percentage: undefined,
        is_recurring: undefined,
      });
      setBelowPercentInput("");
      return;
    }

    // Validate: only digits, one decimal point max, max 2 digits after decimal
    if (!/^\d*\.?\d{0,2}$/.test(value)) {
      setBelowPriceInput(value.slice(0, -1)); // Remove last invalid character
      return;
    }

    const price = parseFloat(value);
    if (isNaN(price)) return;

    const basePrice = currentStockPrice;
    if (!basePrice) return;

    const percentage = ((price - basePrice) / basePrice) * 100;
    const roundedPercent = roundTo2(percentage);

    setWatchlist?.({
      ...watchlist!,
      below_price: roundTo2(price),
      below_percentage: roundedPercent,
    });

    setBelowPercentInput(roundedPercent.toString());
  };

  const handleBelowPercentageChange = (value: string) => {
    setBelowPercentInput(value);

    if (value === "") {
      setWatchlist?.({
        ...watchlist!,
        below_price: undefined,
        below_percentage: undefined,
      });
      setBelowPriceInput("");
      return;
    }

    // Validate: optional minus, digits, one decimal point max, max 2 digits after decimal
    if (!/^-?\d*\.?\d{0,2}$/.test(value)) {
      setBelowPercentInput(value.slice(0, -1)); // Remove last invalid character
      return;
    }

    const percentage = parseFloat(value);
    if (isNaN(percentage)) return;

    const basePrice = currentStockPrice;
    if (!basePrice) return;

    const price = basePrice * (1 + percentage / 100);
    const roundedPrice = roundTo2(price);

    setWatchlist?.({
      ...watchlist!,
      below_price: roundedPrice,
      below_percentage: roundTo2(percentage),
    });

    setBelowPriceInput(roundedPrice.toString());
  };

  const fetchPortfolioFolders = async () => {
    try {
      const response = await fetchWrapper.get(
        "/api/users/portfolio/folders",
        undefined,
        { Authorization: `Bearer ${token}` },
      );
      if (response?.success) {
        const folders =
          response.portfolios?.map((p: any) => ({
            id: p.id,
            name: p.name,
            investment: Number(p.investment ?? 0),
          })) || [];
        setPortfolioFolders(folders);
      }
    } catch (error) {
      console.error("Failed to fetch portfolios", error);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchPortfolioFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when modal opens
  }, [isModalOpen]);

  const canEnableAlerts = abovePriceInput !== "" || belowPriceInput !== "";
  const hasPriceAlertWithoutFrequency =
    isWatchlist &&
    canEnableAlerts &&
    watchlist?.is_recurring !== "everytime" &&
    watchlist?.is_recurring !== "once";

  return (
    <>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2 mb-1">
        {isWatchlist ? (
          <div className="bg-yellow-400 p-1 px-2 rounded-md flex items-center justify-center">
            <Image
              src={WatchlistIcon}
              alt="Watchlist"
              className="w-6 h-6 transition-all duration-200 filter invert dark:invert-0 dark:brightness-100"
            />
          </div>
        ) : (
          <svg
            className="w-6 h-6 text-gray-800 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M8 7V6c0-.6.4-1 1-1h11c.6 0 1 .4 1 1v7c0 .6-.4 1-1 1h-1M3 18v-7c0-.6.4-1 1-1h11c.6 0 1 .4 1 1v7c0 .6-.4 1-1 1H4a1 1 0 0 1-1-1Zm8-3.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z"
            />
          </svg>
        )}
        <span>{heading}</span>
      </h2>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {currentStockSymbol && `${currentStockSymbol} - ₹${currentStockPrice}`}
      </p>

      <div className="space-y-4 max-h-[calc(100vh_-_250px)] xl:max-h-max overflow-y-auto">
        {StatsCard && (
          <SharedSearchedElement
            searchRef={searchRef}
            search={search}
            setSearch={setSearch}
            setAutoCompelte={setAutoComplete}
            handleOnChangeSearchInput={handleOnChangeSearchInput}
            defaultValue={defaultValue}
            autoCompelete={autoComplete}
            filteredData={filteredData}
            getSelectedCompanyData={getSelectedCompanyData}
          />
        )}

        {!isWatchlist ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date
              </label>
              <div className="relative">
                <DateTimePicker
                  fullWidth={true}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  minDate={moment.tz("2010-01-01", "Asia/Kolkata").toDate()}
                />
              </div>
              {listingDateError && (
                <p className="text-red-500 text-sm mt-1">{listingDateError}</p>
              )}
            </div>

            <PortfolioFolderSelector
              portfolioFolders={portfolioFolders}
              selectedFolders={selectedFolders}
              setSelectedFolders={setSelectedFolders}
              setPortfolioFolders={setPortfolioFolders}
              targetIdValue={
                isBuyStock ? preSelectedPortfolioId : targetIdValue
              }
              token={token}
            />

            <div className="flex justify-between gap-3 mb-2">
              <PriceInput
                label="Quantity *"
                value={quantityInput}
                disabled={!currentStockPrice}
                onChange={(val) => {
                  if (val === "") {
                    setQuantityInput(val);
                    if (setQuantity) setQuantity(0);
                    return;
                  }
                  if (/^\d+$/.test(val)) {
                    const num = Number(val);
                    if (num <= MAX_QUANTITY) {
                      setQuantityInput(val);
                      if (setQuantity) setQuantity(num);
                    }
                  }
                }}
                placeholder="Number of shares"
                error={errors.quantity}
              />

              <PriceInput
                label="Buy Price *"
                value={buyPriceInput}
                disabled={!currentStockPrice}
                onChange={(val) => {
                  if (/^\d*\.?\d{0,2}$/.test(val)) {
                    const num = Number(val);
                    if (num <= MAX_PRICE) {
                      setBuyPriceInput(val);
                      if (setBuyPrice) setBuyPrice(num);
                    }
                  }
                }}
                placeholder="Price per share"
                error={errors.buyPrice}
              />
            </div>

            <InvestmentSummary
              investmentImg={investmentImg ?? ""}
              investmentAltText={investmentAltText ?? ""}
              quantity={quantity}
              buyPrice={buyPrice}
              currentPrice={currentValue}
              liquidFunds={totalSelectedInvestment}
              onAddFundsClick={(deficit) => {
                setAddFundAmount(deficit);
                setShowAddFundModal(true);
              }}
            />
          </>
        ) : (
          <div>
            <div className="flex gap-2 justify-between flex-wrap mb-2">
              <div className="flex-1 min-w-[120px]">
                <PriceInput
                  label="Above Price in (₹)"
                  value={abovePriceInput}
                  onChange={handleAbovePriceChange}
                  placeholder="Enter price"
                  error={errors.above}
                />
              </div>

              <div className="flex-1 min-w-[120px]">
                <PriceInput
                  label="Above Price in (%)"
                  value={abovePercentInput}
                  onChange={handleAbovePercentageChange}
                  placeholder="Enter percentage"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-between flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <PriceInput
                  label="Below Price in (₹)"
                  value={belowPriceInput}
                  onChange={handleBelowPriceChange}
                  placeholder="Enter price"
                  error={errors.below}
                />
              </div>

              <div className="flex-1 min-w-[120px]">
                <PriceInput
                  label="Below Price in (%)"
                  value={belowPercentInput}
                  onChange={handleBelowPercentageChange}
                  placeholder="Enter percentage"
                />
              </div>
            </div>
            <label className="mt-2 block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Price Alert (Optional)
            </label>

            <div className="flex items-center gap-4 my-4">
              <AlertFrequencyRadio
                value="everytime"
                label="Everytime"
                checked={watchlist?.is_recurring === "everytime"}
                onChange={(value) =>
                  setWatchlist?.({
                    ...watchlist!,
                    is_recurring: value as "everytime" | "once",
                  })
                }
                disabled={!canEnableAlerts}
              />
              <AlertFrequencyRadio
                value="once"
                label="Once"
                checked={watchlist?.is_recurring === "once"}
                onChange={(value) =>
                  setWatchlist?.({
                    ...watchlist!,
                    is_recurring: value as "everytime" | "once",
                  })
                }
                disabled={!canEnableAlerts}
              />
            </div>
          </div>
        )}

        <div className="mb-0">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes (Optional) - {notes.length}/{maxNotesLength}
          </label>
          <textarea
            value={notes}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= maxNotesLength) {
                setNotes(value);
                if (errors.notes) {
                  setErrors({ ...errors, notes: undefined });
                }
              }
            }}
            placeholder="Investment thesis, target price, stop loss, etc..."
            rows={3}
            maxLength={maxNotesLength}
            className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:outline-none focus-visible:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/40 focus:ring-inset dark:text-white text-sm font-figtree resize-none"
          ></textarea>
          {errors.notes && (
            <p className="text-red-500 text-sm mt-1">{errors.notes}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-2">
        <button
          onClick={() => {
            if (isPortfolio && getData) {
              getData();
            }
            handleOnClick();
          }}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 dark:bg-[#2D2D3A] dark:text-white dark:hover:bg-gray-700 cursor-pointer"
        >
          Cancel
        </button>

        <button
          disabled={
            !currentStockSymbol ||
            hasPriceAlertWithoutFrequency ||
            (!isWatchlist && Boolean(listingDateError)) ||
            (!isWatchlist &&
              totalSelectedInvestment !== undefined &&
              totalSelectedInvestment < 0)
          }
          onClick={isWatchlist ? handleAddToWatchlist : handleAddToPortfolio}
          className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:cursor-not-allowed cursor-pointer ${!currentStockSymbol || hasPriceAlertWithoutFrequency || (!isWatchlist && Boolean(listingDateError)) || (!isWatchlist && totalSelectedInvestment !== undefined && totalSelectedInvestment < 0) ? "opacity-50" : ""}`}
          style={{
            background:
              !currentStockSymbol ||
              hasPriceAlertWithoutFrequency ||
              (!isWatchlist && Boolean(listingDateError)) ||
              (!isWatchlist &&
                totalSelectedInvestment !== undefined &&
                totalSelectedInvestment < 0)
                ? "linear-gradient(0deg, #9CA3AF 0%, #D1D5DB 100%)"
                : "linear-gradient(0deg, #066766 0%, #0FA243 100%)",
          }}
        >
          {heading}
        </button>
      </div>

      <AddFundModal
        isOpen={showAddFundModal}
        onClose={handleCloseAddFundModal}
        portfolios={portfolioFolders.map((p) => ({
          value: p.id,
          label: p.name,
          investment: Number(p.investment || 0),
          currentBalance: 0,
        }))}
        initialPortfolioId={
          selectedFolders.length > 0 ? Number(selectedFolders[0]) : null
        }
        token={token}
        onSuccess={handleSuccessAddFund}
        initialAmount={Math.ceil(addFundAmount)}
        isTopUp={true}
      />
    </>
  );
};

export default memo(ListModal);
